import React from "react";
import { Form, Input, Select, Space, Switch, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  AGGREGATE_HELP,
  CONDITION_HELP,
  CONDITIONS_WITHOUT_VALUE,
  COUNT_AGGREGATES,
  EXPECTED_VALUE_TYPE_OPTIONS,
  ExpectedValueType,
  expectedValueHint,
  isCollectionTarget,
  RULE_AGGREGATES,
  RuleCondition,
  RuleVocabulary,
  TYPED_SCALAR_CONDITIONS,
  validateExpectedValue,
} from "../rules";

export type { RuleFormValues } from "./ruleForm";
export { RULE_FORM_FIELDS, toRulePayload, ruleToFormValues, emptyRuleFormValues } from "./ruleForm";

export interface RuleFormFieldsProps {
  /** What the selects offer; `null` while it loads. */
  vocabulary: RuleVocabulary | null;
  /** A sentence per target entity, shown as an info tooltip in the select. */
  targetHelp?: Record<string, string>;
  /** Shown under the target select (e.g. which targets a stage may not use). */
  targetExtra?: React.ReactNode;
  /** Field-path guidance for the chosen target. */
  fieldPathHint?: (target: string | undefined, collection: boolean) => { extra?: React.ReactNode; placeholder?: string };
}

const defaultFieldPathHint: NonNullable<RuleFormFieldsProps["fieldPathHint"]> = (_target, collection) => ({
  extra: collection
    ? "Dot notation read from each member, e.g. videoMetadata.videoLocalizationType"
    : "Dot notation, e.g. product or customVendorProjectMetadata.projectTags",
});

const withHelp = (value: string, help?: string) => (
  <Space>
    {value}
    {help && (
      <Tooltip title={help}>
        <InfoCircleOutlined style={{ color: "var(--primary-color)" }} />
      </Tooltip>
    )}
  </Space>
);

/**
 * The fields of one rule — target, field path, aggregate (collection targets),
 * condition, value type and expected value, active, description — with the
 * conditional showing and the validation the rules engine implies. Renders
 * inside an antd `<Form>`; the owner supplies the form instance and submit.
 */
const RuleFormFields: React.FC<RuleFormFieldsProps> = ({
  vocabulary,
  targetHelp = {},
  targetExtra,
  fieldPathHint = defaultFieldPathHint,
}) => {
  const targetEntities = vocabulary?.targetEntities ?? [];
  const conditionTypes = vocabulary?.conditionTypes ?? [];
  const aggregates = vocabulary?.aggregates ?? RULE_AGGREGATES;
  const hasCollections = (vocabulary?.collectionTargets ?? []).length > 0;

  const countCompareOf = (get: (name: string) => any) =>
    isCollectionTarget(vocabulary, get("targetEntity")) && COUNT_AGGREGATES.has(get("aggregate") ?? "");

  return (
    <>
      <Form.Item
        name="targetEntity"
        label="Read from"
        rules={[{ required: true, message: "Target entity is required" }]}
        extra={targetExtra}
      >
        <Select
          loading={!vocabulary}
          placeholder="Select which object the field is read from"
          options={targetEntities.map((value) => ({ value, label: withHelp(value, targetHelp[value]) }))}
        />
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.targetEntity !== curr.targetEntity}>
        {({ getFieldValue }) => {
          const target = getFieldValue("targetEntity") as string | undefined;
          const collection = isCollectionTarget(vocabulary, target);
          const hint = fieldPathHint(target, collection);
          return (
            <>
              <Form.Item
                name="fieldPath"
                label="Field Path"
                rules={[{ required: true, whitespace: true, message: "Field Path is required" }]}
                extra={hint.extra}
              >
                <Input placeholder={hint.placeholder} />
              </Form.Item>

              {hasCollections && collection && (
                <Form.Item
                  name="aggregate"
                  label="Aggregate"
                  rules={[{ required: true, message: "Aggregate is required for a collection" }]}
                  extra="How the members of the collection are combined before the condition is applied."
                >
                  <Select
                    placeholder="Select an aggregate"
                    options={aggregates.map((value) => ({ value, label: withHelp(value, AGGREGATE_HELP[value]) }))}
                  />
                </Form.Item>
              )}
            </>
          );
        }}
      </Form.Item>

      <Form.Item name="conditionType" label="Condition" rules={[{ required: true, message: "Condition is required" }]}>
        <Select
          loading={!vocabulary}
          placeholder="Select a condition"
          options={conditionTypes.map((value) => ({ value, label: withHelp(value, CONDITION_HELP[value]) }))}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) =>
          prev.conditionType !== curr.conditionType ||
          prev.valueType !== curr.valueType ||
          prev.aggregate !== curr.aggregate ||
          prev.targetEntity !== curr.targetEntity
        }
      >
        {({ getFieldValue }) => {
          const conditionType = getFieldValue("conditionType") as RuleCondition | undefined;
          if (!conditionType) return null;
          if (CONDITIONS_WITHOUT_VALUE.has(conditionType)) {
            return (
              <Form.Item label="Expected Value">
                <Typography.Text type="secondary">Not used for {conditionType}.</Typography.Text>
              </Form.Item>
            );
          }

          const countCompare = countCompareOf(getFieldValue);
          const valueType = (getFieldValue("valueType") as ExpectedValueType) ?? "string";
          const showValueType = TYPED_SCALAR_CONDITIONS.has(conditionType) && !countCompare;
          const { placeholder, extra } = expectedValueHint({ conditionType, valueType, countCompare });

          return (
            <>
              {showValueType && (
                <Form.Item
                  name="valueType"
                  label="Value type"
                  initialValue="string"
                  extra={'Comparison is strict: 1 and "1" are different. Pick how the value is stored on the data.'}
                >
                  <Select options={EXPECTED_VALUE_TYPE_OPTIONS} />
                </Form.Item>
              )}
              <Form.Item
                name="expectedValue"
                label="Expected Value"
                dependencies={["conditionType", "valueType", "aggregate", "targetEntity"]}
                extra={extra}
                rules={[
                  ({ getFieldValue: get }) => ({
                    validator(_, value) {
                      const message = validateExpectedValue({
                        conditionType: get("conditionType"),
                        raw: value,
                        valueType: get("valueType") ?? "string",
                        countCompare: countCompareOf(get),
                      });
                      return message ? Promise.reject(new Error(message)) : Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder={placeholder} />
              </Form.Item>
            </>
          );
        }}
      </Form.Item>

      <Form.Item name="active" label="Active" valuePropName="checked" initialValue>
        <Switch />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={3} />
      </Form.Item>
    </>
  );
};

export default RuleFormFields;
